import { CallExpressionShape, IdentifierShape, ObjectShape, VariableStatementShape } from '@servicenow/sdk-build-core'
import { ArrowFunctionShape, NowIdShape } from '@servicenow/sdk-build-plugins'
import { ACTIVITY_TABLE, LANE_GENERATED_CALLEE, LANE_DEFAULTS } from '../constants/playbook-plugin-constants'
import { activityRecordToShape } from './activity-config-transform'
import { resolveStartRuleToShape } from '../utils/start-rule-resolver'
import type { Database, Record as SdkRecord, Shape } from '@servicenow/sdk-build-core'
import { findTimerRecord, timerAttributesToShape } from './timer-attributes-transform'
import {
    type ActivityIdentifier,
    type PillTransformContext,
    transformConditionPills,
} from '../utils/playbook-pill-transformer'

export { resolveStartRuleToDatabase as resolveStartRule } from '../utils/start-rule-resolver'

export function buildLaneToShapeResult(
    record: SdkRecord,
    database: Database,
    guidToVarName?: Map<string, string>,
    timerRecords?: SdkRecord[],
    globalActivityMap?: Map<string, ActivityIdentifier>,
    branchGuidMap?: Map<string, { activityVarName: string; fluentBranchId: string }>
): { success: true; value: CallExpressionShape } | { success: false } {
    const { startRuleShape } = resolveStartRuleToShape(record, database, guidToVarName, branchGuidMap)

    // Derive the current lane's variable name from the canonical guidToVarName map.
    // The pill context only contains activities from previously-processed lanes at
    // this point — the current lane's activities are added later in
    // buildActivityCollectionToShape. This is correct because a lane's
    // conditionToRun and timer attributes cannot reference same-lane activities.
    const currentLaneVarName = guidToVarName?.get(record.getId().getValue())
    const pillContext: PillTransformContext | undefined = globalActivityMap
        ? {
              activityMap: globalActivityMap,
              ...(currentLaneVarName ? { currentLaneVarName } : {}),
          }
        : undefined

    // Find matching timer record for this lane
    const timerRecord = timerRecords
        ? findTimerRecord(timerRecords, record.getId().getValue(), 'sys_pd_lane')
        : undefined

    const configShape = record.transform(({ $ }) => ({
        $id: $.val(NowIdShape.from(record)),
        label: $.from('label'),
        name: $.from('name'),
        order: $.from('order').toNumber(),
        description: $.from('description').def(''),
        startRule: $.val(startRuleShape),
        conditionToRun: $.from('condition_to_run')
            .def(LANE_DEFAULTS.CONDITION_TO_RUN)
            .map((v: Shape) => {
                if (!pillContext) {
                    return v
                }
                const str = v.ifString()?.getValue()
                if (!str) {
                    return v
                }
                return transformConditionPills(str, record, pillContext) ?? v
            }),
        restartRule: $.from('restart_rule'),
        ...(timerRecord
            ? {
                  startWithDelay: $.val(timerAttributesToShape(timerRecord, pillContext)),
              }
            : {}),
    }))

    const activityRecords = database.query(ACTIVITY_TABLE, { lane: record.getId().getValue() })
    const activitiesArrow = buildActivityCollectionToShape(
        record,
        activityRecords,
        database,
        globalActivityMap,
        currentLaneVarName,
        timerRecords
    )

    const laneArg = new ObjectShape({
        source: record,
        properties: {
            config: configShape,
            activities: activitiesArrow,
        },
    })

    return {
        success: true,
        value: new CallExpressionShape({
            source: record,
            callee: LANE_GENERATED_CALLEE,
            args: [laneArg],
        }),
    }
}

/**
 * Build activity collection with branch-aware resolution.
 *
 * This is a specialized version of buildChildCollectionToShape that:
 * 1. Topologically sorts activities so dependencies come first
 * 2. Populates the shared globalActivityMap with this lane's activities
 * 3. Processes activities in order, accumulating branch GUID maps from Decision activities
 * 4. Passes the accumulated branch map to each activity's toShape so that
 *    starts_after_branches GUIDs resolve to PropertyAccessShape references
 */
function buildActivityCollectionToShape(
    source: SdkRecord,
    activityRecords: SdkRecord[],
    database: Database,
    globalActivityMap?: Map<string, ActivityIdentifier>,
    currentLaneVarName?: string,
    timerRecords?: SdkRecord[]
): ArrowFunctionShape {
    // Topologically sort so dependencies (starts_after_activities) come first
    const sortedRecords = topologicalSort(activityRecords, (rec) => {
        const raw = rec.get('starts_after_activities')?.ifString()?.getValue() ?? ''
        return raw
            .split(',')
            .map((g) => g.trim())
            .filter(Boolean)
    })

    // Build guid → varName map and populate the shared global activity map
    // so that same-lane and cross-lane pill references resolve correctly.
    const activityGuidToVarName = new Map<string, string>()
    for (const [i, rec] of sortedRecords.entries()) {
        const varName = `activity_${i}`
        const guid = rec.getId().getValue()
        activityGuidToVarName.set(guid, varName)
        globalActivityMap?.set(guid, {
            varName,
            ...(currentLaneVarName ? { laneVarName: currentLaneVarName } : {}),
        })
    }

    // Build pill context for activities in this lane
    const pillContext: PillTransformContext | undefined = globalActivityMap
        ? {
              activityMap: globalActivityMap,
              ...(currentLaneVarName ? { currentLaneVarName } : {}),
          }
        : undefined

    // Process each activity, accumulating branch maps
    // branchGuidMap: branch GUID → { activityVarName, fluentBranchId }
    const branchGuidMap = new Map<string, { activityVarName: string; fluentBranchId: string }>()

    const childShapesWithIndex: { shape: CallExpressionShape; originalIndex: number }[] = []
    for (const [i, rec] of sortedRecords.entries()) {
        const result = activityRecordToShape(
            rec,
            database,
            activityGuidToVarName,
            branchGuidMap,
            pillContext,
            timerRecords
        )
        if (result.success) {
            childShapesWithIndex.push({ shape: result.value, originalIndex: i })

            // If this is a Decision activity with a branch map, register all branches
            if (result.branchMap) {
                const activityGuid = rec.getId().getValue()
                const activityVarName = activityGuidToVarName.get(activityGuid) ?? `activity_${i}`
                for (const [branchGuid, fluentBranchId] of result.branchMap) {
                    branchGuidMap.set(branchGuid, { activityVarName, fluentBranchId })
                }
            }
        }
    }

    // Empty collection: concise form `() => ({})`
    if (childShapesWithIndex.length === 0) {
        return new ArrowFunctionShape({
            source,
            parameters: [],
            returnValue: new ObjectShape({ source, properties: {} }),
            implicitReturn: true,
        })
    }

    // Non-empty: variable declarations + return object
    const statements: Shape[] = []
    const returnEntries: Record<string, Shape> = {}
    for (const { shape, originalIndex } of childShapesWithIndex) {
        const varName = `activity_${originalIndex}`
        statements.push(new VariableStatementShape({ source, variableName: varName, initializer: shape }))
        returnEntries[varName] = new IdentifierShape({ source, name: varName })
    }

    const returnValue = new ObjectShape({ source, properties: returnEntries })

    return new ArrowFunctionShape({
        source,
        parameters: [],
        statements,
        returnValue,
    })
}

/**
 * Topologically sort records so that dependencies come before dependents.
 * Falls back to original order for records with no dependency relationships.
 */
function topologicalSort(records: SdkRecord[], getDependencyGuids: (record: SdkRecord) => string[]): SdkRecord[] {
    const guidToRecord = new Map<string, SdkRecord>()
    for (const rec of records) {
        guidToRecord.set(rec.getId().getValue(), rec)
    }

    const sorted: SdkRecord[] = []
    const visited = new Set<string>()
    const visiting = new Set<string>()

    function visit(rec: SdkRecord) {
        const guid = rec.getId().getValue()
        if (visited.has(guid)) {
            return
        }
        if (visiting.has(guid)) {
            return // cycle — break it by skipping
        }
        visiting.add(guid)

        for (const depGuid of getDependencyGuids(rec)) {
            const depRecord = guidToRecord.get(depGuid)
            if (depRecord) {
                visit(depRecord)
            }
        }

        visiting.delete(guid)
        visited.add(guid)
        sorted.push(rec)
    }

    for (const rec of records) {
        visit(rec)
    }

    return sorted
}
